import { useEffect, useState, useMemo } from "react";
import { PageHero } from "@/components/public/Layout";
import { Reveal, SectionLabel } from "@/components/public/shared";
import { useEnquiry } from "@/components/public/EnquiryProvider";
import { api } from "@/lib/api";
import { IMG } from "@/lib/images";
import { Check } from "lucide-react";

export default function Packages() {
  const { open } = useEnquiry();
  const [pkgs, setPkgs] = useState([]);
  const [occ, setOcc] = useState("All");

  useEffect(() => { api.get("/packages").then(({ data }) => setPkgs(data)).catch(() => setPkgs([])); }, []);
  const occasions = useMemo(() => ["All", ...Array.from(new Set(pkgs.map((p) => p.occasion).filter(Boolean)))], [pkgs]);
  const filtered = pkgs.filter((p) => occ === "All" || p.occasion === occ);

  return (
    <div>
      <PageHero label="Curated Menu Packages" title="Wedding & festive menus, thoughtfully composed." subtitle="Browse our curated pure-vegetarian & Jain-Friendly packages for weddings, festivals, corporate galas and intimate celebrations — then enquire to personalise yours." image={IMG.indianWeddingBuffet} />

      <section className="bg-ivory py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-10 flex flex-wrap justify-center gap-2">
            {occasions.map((o) => (
              <button key={o} data-testid={`packages-filter-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setOcc(o)}
                className={`rounded-full border px-4 py-2 text-[12px] font-medium uppercase tracking-wider transition-colors ${occ === o ? "border-espresso bg-espresso text-ivory" : "border-subtle text-espresso/70 hover:border-gold hover:text-gold"}`}>
                {o}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="packages-grid">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 60}>
                <div className="group flex h-full flex-col overflow-hidden border border-subtle bg-cream">
                  <div className="relative overflow-hidden arch-top aspect-[4/3]">
                    <img src={p.image_url || IMG.indianThali} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                    {p.occasion && <span className="absolute left-3 top-3 rounded-full bg-espresso/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-ivory">{p.occasion}</span>}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-2xl text-espresso">{p.title}</h3>
                    <div className="mt-1 flex items-baseline gap-2">
                      <span className="font-display text-2xl text-gold">₹{p.price}</span>
                      <span className="text-xs uppercase tracking-wider text-inkmuted">{p.price_unit || "per plate"}</span>
                      {p.min_guests && <span className="ml-auto text-xs text-inkmuted">Min {p.min_guests}</span>}
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-inkmuted">{p.description}</p>
                    {(p.inclusions || []).length > 0 && (
                      <ul className="mt-4 space-y-2">
                        {p.inclusions.map((inc) => (
                          <li key={inc} className="flex items-start gap-2 text-sm text-espresso/85"><Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" /> {inc}</li>
                        ))}
                      </ul>
                    )}
                    <button data-testid={`package-enquire-${i}`} onClick={() => open("event")}
                      className="mt-6 w-full bg-espresso py-3 text-[12px] font-semibold uppercase tracking-editorial text-ivory transition-colors hover:bg-gold">
                      Enquire About This Package
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          {filtered.length === 0 && <p className="py-16 text-center text-inkmuted">No packages in this selection yet.</p>}
        </div>
      </section>
    </div>
  );
}
